/**
 * The plural forms i18next looks up for a key (`key_one`, `key_other`…) and the ones a locale can use.
 *
 * `zero` is a valid form in EVERY language: i18next looks `key_zero` up whenever `count === 0`, whatever the plural
 * rules of the language say (`i18next.js:775` in 25.4.2), so a catalogue may carry it for any locale.
 */

export const PLURAL_FORMS = ['zero', 'one', 'two', 'few', 'many', 'other'] as const

export type PluralForm = (typeof PLURAL_FORMS)[number]

/**
 * Splits a key into its base and its plural form: `shared.bulkActions.selected_other` is
 * `{ baseKey: 'shared.bulkActions.selected', form: 'other' }`. A key with no plural suffix (`page.loginTitle`), a
 * trailing underscore (`a.b_`) and a key that is only the suffix (`_other`) are `null`.
 */
export function splitPluralKey(key: string): { baseKey: string; form: PluralForm } | null {
  const underscore = key.lastIndexOf('_')
  if (underscore <= 0) return null
  const form = key.slice(underscore + 1)
  const found = PLURAL_FORMS.find((candidate) => candidate === form)
  return found === undefined ? null : { baseKey: key.slice(0, underscore), form: found }
}

const oneCoversMore = new Map<string, boolean>()

/**
 * True when the `one` form of a language covers a number other than 1 (Russian and Ukrainian: 21, 31… ; French and
 * Portuguese: 0), so a text for `key_one` is shown with those numbers too and must carry the count: «Un seleccionado»
 * would be a false figure for 21. Where `one` is only 1 the text may say «one» in words.
 *
 * It looks at 0 to 100 and at 0.5, 1.5 and 2.5. That is enough for the CLDR rules of the languages the catalogues have
 * (the `one` of those rules repeats with the tens: 1, 21, 31… or covers 0 or the decimals just above 1); a language
 * whose `one` only shows up past 100 would be missed.
 */
export function oneCoversMoreThanOne(locale: string): boolean {
  const known = oneCoversMore.get(locale)
  if (known !== undefined) return known
  const rules = new Intl.PluralRules(locale)
  const candidates = [0.5, 1.5, 2.5, ...Array.from({ length: 101 }, (_, count) => count)]
  const covers = candidates.some((count) => count !== 1 && rules.select(count) === 'one')
  oneCoversMore.set(locale, covers)
  return covers
}

/** The plural forms a locale uses (CLDR, from `Intl.PluralRules`) and `zero`, in the order of `PLURAL_FORMS`. */
export function pluralCategoriesOf(locale: string): readonly PluralForm[] {
  const categories = new Set<string>(
    new Intl.PluralRules(locale).resolvedOptions().pluralCategories,
  )
  categories.add('zero')
  return PLURAL_FORMS.filter((form) => categories.has(form))
}
